import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ref, readonly } from 'vue';

// ==========================================
// CONSTANTES DE ROLES
// ==========================================

export const AGENT_ROLES = {
  MASTER: 'master',
  ASSOCIATED: 'associated', 
  BASIC: 'basic'
};

export const BASIC_AGENT_LIMITS = {
  MAX_COLIVINGS: 10,
  MAX_ROOMS: 5, // Límite máximo de habitaciones por coliving para agentes Basic
  RESTRICTED_ROUTES: [
    '/agent-dashboard/clients',
    '/agent-dashboard/team'
  ],
  RESTRICTED_COMPONENTS: [
    'ClientManagement',
    'VerificationManagement',
    'TeamManagement'
  ]
};

// ==========================================
// CACHE DE INFORMACIÓN DE AGENTE
// ==========================================

let agentInfoCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

// ==========================================
// FUNCIONES PRINCIPALES
// ==========================================

/**
 * Obtiene la información completa del agente actual
 * @returns {Promise<Object|null>} Información del agente o null
 */
export async function getCurrentAgentInfo() {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return null;
    }

    // Verificar cache válido
    const now = Date.now();
    if (agentInfoCache && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
      return agentInfoCache;
    }

    // Obtener información fresca del usuario
    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data();
    if (userData.userType !== 'agent') {
      return null;
    }

    const agentInfo = {
      id: currentUser.uid,
      userType: userData.userType,
      agentRole: userData.agentRole || AGENT_ROLES.MASTER, // Default para compatibilidad
      masterAgentId: userData.masterAgentId,
      permissions: userData.permissions || {},
      email: userData.email,
      displayName: userData.displayName || userData.fullName,
      teamMembers: userData.teamMembers || [],
      isHivePartner: userData.isHivePartner === true,
      isLeadPartnerAgent: userData.isLeadPartnerAgent === true || userData.isHivePartner === true,
    };

    // Actualizar cache
    agentInfoCache = agentInfo;
    cacheTimestamp = now;

    return agentInfo;
  } catch (error) {
    console.error('[getCurrentAgentInfo] Error:', error);
    return null;
  }
}

/**
 * Invalida el cache de información del agente
 */
export function invalidateAgentCache() {
  agentInfoCache = null;
  cacheTimestamp = null;
}

/**
 * Verifica si el usuario actual es un agente básico
 * @returns {Promise<boolean>}
 */
export async function isCurrentUserBasicAgent() {
  try {
    const agentInfo = await getCurrentAgentInfo();
    return agentInfo?.agentRole === AGENT_ROLES.BASIC;
  } catch (error) {
    console.error('[isCurrentUserBasicAgent] Error:', error);
    return false;
  }
}

/**
 * Verifica si el usuario actual es un agente master
 * @returns {Promise<boolean>}
 */
export async function isCurrentUserMasterAgent() {
  try {
    const agentInfo = await getCurrentAgentInfo();
    return agentInfo?.agentRole === AGENT_ROLES.MASTER;
  } catch (error) {
    console.error('[isCurrentUserMasterAgent] Error:', error);
    return false;
  }
}

/**
 * Verifica si el usuario actual es un agente asociado
 * @returns {Promise<boolean>}
 */
export async function isCurrentUserAssociatedAgent() {
  try {
    const agentInfo = await getCurrentAgentInfo();
    return agentInfo?.agentRole === AGENT_ROLES.ASSOCIATED;
  } catch (error) {
    console.error('[isCurrentUserAssociatedAgent] Error:', error);
    return false;
  }
}

/** Agentes que operan leads (p. ej. Grupo Hive): sin onboarding ni datos de contrato obligatorios */
export function isLeadPartnerAgentInfo(agentInfo) {
  if (!agentInfo) return false;
  return agentInfo.isLeadPartnerAgent === true || agentInfo.isHivePartner === true;
}

export async function isCurrentUserLeadPartnerAgent() {
  try {
    const agentInfo = await getCurrentAgentInfo();
    return isLeadPartnerAgentInfo(agentInfo);
  } catch (error) {
    console.error('[isCurrentUserLeadPartnerAgent] Error:', error);
    return false;
  }
}

// ==========================================
// FUNCIONES DE PERMISOS
// ==========================================

/**
 * Verifica si el usuario actual tiene un permiso específico
 * @param {string} permission - Nombre del permiso a verificar
 * @returns {Promise<boolean>}
 */
export async function hasCurrentUserPermission(permission) {
  try {
    const agentInfo = await getCurrentAgentInfo();
    if (!agentInfo) return false;

    // Master agents tienen todos los permisos
    if (agentInfo.agentRole === AGENT_ROLES.MASTER) {
      return true;
    }

    // Verificar permiso específico
    return agentInfo.permissions?.[permission] === true;
  } catch (error) {
    console.error('[hasCurrentUserPermission] Error:', error);
    return false;
  }
}

/**
 * Verifica si el usuario puede gestionar clientes
 * @returns {Promise<boolean>}
 */
export async function canCurrentUserManageClients() {
  return await hasCurrentUserPermission('canManageClients');
}

/**
 * Verifica si el usuario puede gestionar equipos
 * @returns {Promise<boolean>}
 */
export async function canCurrentUserManageTeam() {
  try {
    const agentInfo = await getCurrentAgentInfo();
    // Solo master agents pueden gestionar equipos
    return agentInfo?.agentRole === AGENT_ROLES.MASTER;
  } catch (error) {
    console.error('[canCurrentUserManageTeam] Error:', error);
    return false;
  }
}

/**
 * Verifica si el usuario puede ver reportes
 * @returns {Promise<boolean>}
 */
export async function canCurrentUserViewReports() {
  return await hasCurrentUserPermission('canViewReports');
}

// ==========================================
// FUNCIONES DE NAVEGACIÓN Y RUTAS
// ==========================================

/**
 * Verifica si una ruta está restringida para el usuario actual
 * @param {string} routePath - Ruta a verificar
 * @returns {Promise<boolean>}
 */
export async function isRouteRestrictedForCurrentUser(routePath) {
  try {
    const isBasic = await isCurrentUserBasicAgent();
    if (!isBasic) return false;

    return BASIC_AGENT_LIMITS.RESTRICTED_ROUTES.some(restrictedRoute => 
      routePath.includes(restrictedRoute)
    );
  } catch (error) {
    console.error('[isRouteRestrictedForCurrentUser] Error:', error);
    return false;
  }
}

/**
 * Verifica si un componente está restringido para el usuario actual
 * @param {string} componentName - Nombre del componente a verificar
 * @returns {Promise<boolean>}
 */
export async function isComponentRestrictedForCurrentUser(componentName) {
  try {
    const isBasic = await isCurrentUserBasicAgent();
    if (!isBasic) return false;

    return BASIC_AGENT_LIMITS.RESTRICTED_COMPONENTS.includes(componentName);
  } catch (error) {
    console.error('[isComponentRestrictedForCurrentUser] Error:', error);
    return false;
  }
}

// ==========================================
// FUNCIONES DE UI/UX
// ==========================================

/**
 * Genera la información para mostrar un mensaje de upgrade
 * @param {string} feature - Nombre de la funcionalidad restringida
 * @returns {Promise<Object>}
 */
export async function getUpgradePromptInfo(feature) {
  try {
    const agentInfo = await getCurrentAgentInfo();
    
    return {
      currentRole: agentInfo?.agentRole || 'unknown',
      isUpgradeRequired: agentInfo?.agentRole === AGENT_ROLES.BASIC,
      feature,
      upgradeMessage: `La funcionalidad "${feature}" requiere actualizar tu plan`,
      contactMessage: 'Contacta con soporte para actualizar tu plan',
      availablePlans: agentInfo?.agentRole === AGENT_ROLES.BASIC ? ['Associated', 'Master'] : ['Master']
    };
  } catch (error) {
    console.error('[getUpgradePromptInfo] Error:', error);
    return {
      currentRole: 'unknown',
      isUpgradeRequired: false,
      feature,
      upgradeMessage: 'Funcionalidad no disponible',
      contactMessage: 'Contacta con soporte',
      availablePlans: []
    };
  }
}

/**
 * Obtiene la clase CSS para el badge del rol del agente
 * @param {string} role - Rol del agente
 * @returns {string}
 */
export function getRoleBadgeClass(role) {
  const classes = {
    [AGENT_ROLES.MASTER]: 'bg-purple-100 text-purple-800 border-purple-200',
    [AGENT_ROLES.ASSOCIATED]: 'bg-blue-100 text-blue-800 border-blue-200', 
    [AGENT_ROLES.BASIC]: 'bg-gray-100 text-gray-800 border-gray-200'
  };
  
  return `px-2 py-1 text-xs font-medium rounded-full border ${classes[role] || classes[AGENT_ROLES.BASIC]}`;
}

/**
 * Obtiene el texto display para el rol del agente
 * @param {string} role - Rol del agente
 * @returns {string}
 */
export function getRoleDisplayText(role) {
  const displayTexts = {
    [AGENT_ROLES.MASTER]: 'Agente Master',
    [AGENT_ROLES.ASSOCIATED]: 'Agente Asociado',
    [AGENT_ROLES.BASIC]: 'Agente Básico'
  };
  
  return displayTexts[role] || 'Agente';
}

// ==========================================
// FUNCIONES REACTIVAS PARA VUE
// ==========================================

/**
 * Hook reactivo para obtener información del agente en tiempo real
 * @returns {Object} Estado reactivo con información del agente
 */
export function useAgentInfo() {
  const agentInfo = ref(null);
  const isLoading = ref(true);
  const error = ref(null);

  const loadAgentInfo = async () => {
    try {
      isLoading.value = true;
      error.value = null;
      agentInfo.value = await getCurrentAgentInfo();
    } catch (err) {
      error.value = err;
      console.error('[useAgentInfo] Error:', err);
    } finally {
      isLoading.value = false;
    }
  };

  // Cargar información inicial
  loadAgentInfo();

  // Recargar cuando cambie la autenticación
  const auth = getAuth();
  auth.onAuthStateChanged(() => {
    invalidateAgentCache();
    loadAgentInfo();
  });

  return {
    agentInfo: readonly(agentInfo),
    isLoading: readonly(isLoading),
    error: readonly(error),
    refresh: loadAgentInfo
  };
}

// ==========================================
// FUNCIONES DE LÍMITES PARA AGENTES BÁSICOS
// ==========================================

/**
 * Verifica si el usuario actual puede crear un nuevo coliving
 * @param {Array} currentColivings - Lista actual de colivings del agente
 * @returns {Promise<Object>} Objeto con información sobre si puede crear y los límites
 */
export async function canCurrentUserCreateColiving(currentColivings = []) {
  try {
    const agentInfo = await getCurrentAgentInfo();
    if (!agentInfo) {
      return {
        canCreate: false,
        reason: 'No se pudo obtener información del agente',
        currentCount: 0,
        maxAllowed: 0
      };
    }

    // Master agents pueden crear colivings ilimitados
    if (agentInfo.agentRole === AGENT_ROLES.MASTER) {
      return {
        canCreate: true,
        reason: 'Agente Master - sin límite',
        currentCount: currentColivings.length,
        maxAllowed: 'unlimited'
      };
    }

    // Associated agents heredan los límites de su master
    if (agentInfo.agentRole === AGENT_ROLES.ASSOCIATED) {
      return {
        canCreate: true,
        reason: 'Agente Asociado - gestiona colivings del Master',
        currentCount: currentColivings.length,
        maxAllowed: 'shared_with_master'
      };
    }

    // Basic agents tienen límite de 10 colivings
    if (agentInfo.agentRole === AGENT_ROLES.BASIC) {
      const currentCount = currentColivings.length;
      const maxAllowed = BASIC_AGENT_LIMITS.MAX_COLIVINGS;
      const canCreate = currentCount < maxAllowed;

      return {
        canCreate,
        reason: canCreate 
          ? `Puedes crear ${maxAllowed - currentCount} coliving(s) más`
          : `Has alcanzado el límite de ${maxAllowed} coliving(s)`,
        currentCount,
        maxAllowed,
        isAtLimit: currentCount >= maxAllowed
      };
    }

    // Fallback para roles desconocidos
    return {
      canCreate: false,
      reason: 'Tipo de agente no reconocido',
      currentCount: currentColivings.length,
      maxAllowed: 0
    };
  } catch (error) {
    console.error('[canCurrentUserCreateColiving] Error:', error);
    return {
      canCreate: false,
      reason: 'Error verificando permisos',
      currentCount: 0,
      maxAllowed: 0
    };
  }
}

/**
 * Obtiene información de upgrade para agentes Basic que han alcanzado límites
 * @param {string} featureName - Nombre de la funcionalidad limitada
 * @param {Object} limitInfo - Información sobre el límite actual
 * @returns {Object} Información para mostrar prompt de upgrade
 */
export function getBasicAgentUpgradeInfo(featureName, limitInfo = {}) {
  return {
    title: `Límite de ${featureName} alcanzado`,
    message: `Los agentes Basic pueden gestionar un máximo de ${limitInfo.maxAllowed} ${featureName.toLowerCase()}${limitInfo.maxAllowed > 1 ? 's' : ''}.`,
    currentUsage: `Actualmente tienes ${limitInfo.currentCount} de ${limitInfo.maxAllowed}`,
    upgradeMessage: 'Actualiza a Agente Master para gestionar colivings ilimitados',
    contactInfo: 'Contacta con soporte para actualizar tu plan',
    upgradeOptions: [
      {
        plan: 'Agente Master',
        benefits: ['Colivings ilimitados', 'Gestión de equipos', 'Acceso a todas las funcionalidades'],
        recommended: true
      }
    ]
  };
}

